import axios from 'axios';
import { from, of } from 'rxjs';


// Workspace endpoints are v2 (plain JSON, errors as application/problem+json):
// callers read `res.data` of the axios response, there is no `success` flag.
// Every document and workspace a call names is its exposed id (a UUID; the
// answers' docExposedId / workspaceExposedId / exposedId, 2026-10-13): a
// numeric id is refused with 400.
class WorkSpaceService {
    getWorkSpaces = (options) => {
        let promise = axios.get(`${globalConfig.api_URL}api/v2/private/workspace`, options);
        return from(promise);
    }

    // one page of members, in the order they were added; the next page with
    // the previous answer's pageInfo.next (null on the last page)
    getWorkSpaceUsers = (workSpaceId, cursor) => {
        let promise = axios.get(`${globalConfig.api_URL}api/v2/private/workspace/${workSpaceId}/user`,
            { params: cursor ? { cursor } : {} });
        return from(promise);
    }

    // v2: {rights, visualSignature, allowedClientHashAlgorithms, grantedSigners,
    // grantedKinds, shareId, requiredSignatory (masked)}
    getSigningOptions = (docId, options) => {
        let promise = axios.get(`${globalConfig.api_URL}api/v2/private/document/${docId}/signing-options`, options);
        return from(promise);
    }

    // roles offered when adding a user (v1 common-controller, no v2 yet)
    getRoles = () => {
        let promise = axios.get(`${globalConfig.api_URL}api/private/listrole`, { objectResponse: true });
        return from(promise);
    }

    // no workspace (a corporate login before it picks one): nothing to ask,
    // the empty list - 0 is no exposed id
    getDocumentList = (workSpaceId, timeStamp) => {
        if (!workSpaceId) return of({ success: true, data: { pdfList: [], timeStamp: 0 } });
        let promise = axios.get(`${globalConfig.api_URL}api/private/listvalidateddocs/${workSpaceId}/${timeStamp || 0}`, { objectResponse: true });
        return from(promise);
    }

    // v2 read: DocumentValidationResult in the body (no envelope), its version
    // in ETag; refusals as problem+json (400 INVALID_INPUT, 403 SHARE_WITHDRAWN
    // or LINK_SCOPE, 410 DOCUMENT_DELETED, 503 ORGANIZATIONS_UNAVAILABLE)
    getDocumentResult = (docId, options) => {
        let promise = axios.get(`${globalConfig.api_URL}api/v2/private/document/${docId}`, options);
        return from(promise);
    }

    // A free validation (the public form) is validated only once its document
    // is opened (share-prompt task 22): call this whenever a document opens,
    // through a share link or signed in - never in a public view. 204 whatever
    // the document (started, started already, nothing to start); a 5xx means
    // not queued, and the next opening tries again. No error window.
    startFreeValidation = (docId) => {
        let promise = axios.post(`${globalConfig.api_URL}api/v2/private/free-validations/${docId}/start`, null,
            { skipCustomErrorHandling: true });
        return from(promise);
    }

    // signed in only: the caller's free validations never opened, the newest
    // first, at most 100 -> { items: [{ docId, docExposedId, workspaceId,
    // workspaceExposedId, fileName, requestedAt }] };
    // in the main workspace, yet in no document list until opened
    getPendingFreeValidations = () => {
        let promise = axios.get(`${globalConfig.api_URL}api/v2/private/free-validations/pending`,
            { skipCustomErrorHandling: true });
        return from(promise);
    }

    deleteDocument = (docId) => {
        let promise = axios.delete(`${globalConfig.api_URL}api/private/deletedocument/${docId}`, { objectResponse: true });
        return from(promise);
    }

    addUserToWorkSpace = (workSpaceId, email, role) => {
        let promise = axios.post(`${globalConfig.api_URL}api/v2/private/workspace/${workSpaceId}/user`, { email, role });
        return from(promise);
    }

    // userID: the member's account id (the list's userId; one's own
    // userInfo.id to leave); workspaceID: the workspace's exposed id
    deleteUserFromWorkSpace = (userID, workspaceID) => {
        let promise = axios.delete(`${globalConfig.api_URL}api/v2/private/workspace/${workspaceID}/user/${userID}`);
        return from(promise);
    }

    changedocheader = (docId, value) => {
        let promise = axios.post(`${globalConfig.api_URL}api/private/changedocheader`, {id: docId, value: value}, { objectResponse: true });
        return from(promise);
    }
}

export default new WorkSpaceService();